// LinkifiedText — render plain text with bare URLs turned into clickable links.
//
// Built entirely from React nodes (never dangerouslySetInnerHTML): opportunity
// descriptions are scraped, untrusted text, so injecting them as HTML would be
// an XSS hole. Only http(s) and www. URLs are linkified — `javascript:`/`data:`
// schemes never match, so a hostile description cannot produce an active link.
//
// Trailing sentence punctuation (".", ")", etc.) is pushed back into the text
// run so "see https://x.gov/p." links to the URL, not the URL-plus-period.

import { Fragment, type ReactNode } from 'react';

// http(s):// … or a bare www. host, up to the next whitespace or `<`.
const URL_RE = /\b(?:https?:\/\/|www\.)[^\s<]+/gi;
// Punctuation that is almost always sentence trailing, not part of the URL.
const TRAILING_PUNCT = /[.,;:!?)\]}'"»]+$/;

export function LinkifiedText({ text }: { text: string }) {
  const nodes: ReactNode[] = [];
  let cursor = 0;
  let key = 0;
  URL_RE.lastIndex = 0;

  for (let m = URL_RE.exec(text); m !== null; m = URL_RE.exec(text)) {
    const start = m.index;
    let raw = m[0];

    // Trim trailing punctuation back out of the link target.
    const trail = TRAILING_PUNCT.exec(raw);
    if (trail) raw = raw.slice(0, raw.length - trail[0].length);
    if (raw.length === 0) continue;

    const href = raw.startsWith('www.') ? `https://${raw}` : raw;

    if (start > cursor) nodes.push(<Fragment key={key++}>{text.slice(cursor, start)}</Fragment>);
    nodes.push(
      <a
        key={key++}
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        style={{ color: 'var(--cyan)', textDecoration: 'underline', wordBreak: 'break-all' }}
      >
        {raw}
      </a>,
    );
    // Resume after the trimmed URL so any trailing punctuation renders as text.
    cursor = start + raw.length;
  }

  if (cursor < text.length) nodes.push(<Fragment key={key++}>{text.slice(cursor)}</Fragment>);
  return <>{nodes}</>;
}
